import { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Header } from "@/components/Header";
import { BackButton } from "@/components/BackButton";
import { StarField } from "@/components/alignment/StarField";
import { api, apiErr } from "@/lib/api";
import { toast } from "sonner";
import QRCode from "react-qr-code";
import { Copy, Check, Loader2, CircleCheck, CircleDashed, ShieldCheck, CreditCard, Bitcoin, Lock, RefreshCw, ArrowLeft, Sparkles } from "lucide-react";

const TIER_COLOR = { bronze: "#E08D3C", silver: "#C7D2DE", gold: "#F5C842" };

const SPIRITUAL_BG =
  "radial-gradient(1100px 600px at 15% -10%, rgba(37,52,120,0.55), transparent 60%), radial-gradient(1000px 700px at 90% 0%, rgba(88,28,135,0.5), transparent 60%), radial-gradient(900px 900px at 50% 120%, rgba(14,116,144,0.32), transparent 60%), linear-gradient(180deg,#070512 0%,#0A0A14 100%)";

export default function Payment() {
  const { tier } = useParams();
  const [method, setMethod] = useState(null); // null | "card" | "btc"
  const [usd, setUsd] = useState(null);
  const color = TIER_COLOR[tier] || "#A78BFA";

  useEffect(() => {
    api.get("/pricing").then((r) => setUsd(r.data.tiers?.[tier]?.usd)).catch(() => {});
  }, [tier]);

  return (
    <div className="min-h-screen relative text-slate-200 overflow-hidden">
      <div className="absolute inset-0" style={{ background: SPIRITUAL_BG }} />
      <StarField color={color} />
      <div className="relative z-10">
        <Header />
        <div className="max-w-4xl mx-auto px-6 py-12">
          <BackButton className="mb-6" />

          <p className="text-sm tracking-[0.3em] uppercase text-violet-300/80 mb-2">Unlock your subscription</p>
          <h1 className="font-head font-900 text-4xl sm:text-5xl leading-[0.95] mb-3" style={{ color }}>{(tier || "").toUpperCase()}</h1>
          <p className="text-slate-400 mb-10 text-base">
            {usd != null ? `≈ $${usd} USD` : "…"} <span className="text-slate-600">· live BTC price</span>
          </p>

          {!method && (
            <div className="grid sm:grid-cols-2 gap-5" data-testid="method-select">
              <button
                data-testid="method-card"
                onClick={() => setMethod("card")}
                className="group text-left rounded-2xl border border-violet-400/25 bg-white/[0.03] backdrop-blur-md p-7 transition-transform duration-300 hover:-translate-y-1"
                style={{ boxShadow: "0 0 50px rgba(167,139,250,0.10)" }}
              >
                <CreditCard className="w-9 h-9 text-[#A78BFA] mb-4" />
                <h2 className="font-head font-700 text-xl text-slate-50 mb-2">Card / Apple Pay</h2>
                <p className="text-sm text-slate-400">Pay {usd != null ? `$${usd}` : "the USD equivalent"} instantly with Apple Pay, Google Pay or any card via Stripe.</p>
              </button>
              <button
                data-testid="method-btc"
                onClick={() => setMethod("btc")}
                className="group text-left rounded-2xl border border-amber-300/25 bg-white/[0.03] backdrop-blur-md p-7 transition-transform duration-300 hover:-translate-y-1"
                style={{ boxShadow: "0 0 50px rgba(245,200,66,0.10)" }}
              >
                <Bitcoin className="w-9 h-9 text-[#F5C842] mb-4" />
                <h2 className="font-head font-700 text-xl text-slate-50 mb-2">Bitcoin (on-chain)</h2>
                <p className="text-sm text-slate-400">Send BTC to our address and submit your transaction hash as proof of payment.</p>
              </button>
            </div>
          )}

          {method === "card" && <CardPay tier={tier} color={color} onBack={() => setMethod(null)} />}
          {method === "btc" && <BtcPay tier={tier} color={color} onBack={() => setMethod(null)} />}
        </div>
      </div>
    </div>
  );
}

function ChangeMethod({ onBack }) {
  return (
    <button
      onClick={onBack}
      data-testid="change-method"
      className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-slate-100 transition-colors duration-300 mb-6"
    >
      <ArrowLeft className="w-4 h-4" /> Change method
    </button>
  );
}

function CardPay({ tier, color, onBack }) {
  const [quote, setQuote] = useState(null);
  const [remaining, setRemaining] = useState(0);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);

  const fetchQuote = useCallback(async () => {
    setFetching(true);
    try {
      const { data } = await api.post("/payments/quote", { tier });
      setQuote(data);
      setRemaining(Math.max(0, Math.floor((new Date(data.expires_at) - new Date()) / 1000)));
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setFetching(false);
    }
  }, [tier]);

  useEffect(() => { fetchQuote(); }, [fetchQuote]);

  useEffect(() => {
    if (remaining <= 0) return;
    const id = setInterval(() => setRemaining((r) => Math.max(0, r - 1)), 1000);
    return () => clearInterval(id);
  }, [quote?.quote_id]);

  const expired = quote && remaining <= 0;
  const mmss = `${String(Math.floor(remaining / 60)).padStart(2, "0")}:${String(remaining % 60).padStart(2, "0")}`;

  const pay = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/payments/checkout", { tier, origin_url: window.location.origin, quote_id: quote.quote_id });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(apiErr(e));
      setLoading(false);
      if (String(apiErr(e)).toLowerCase().includes("quote")) fetchQuote();
    }
  };

  return (
    <div className="max-w-lg" data-testid="card-pay">
      <ChangeMethod onBack={onBack} />
      <div className="rounded-2xl border border-violet-400/25 bg-white/[0.03] backdrop-blur-md p-8" style={{ boxShadow: "0 0 60px rgba(167,139,250,0.10)" }}>
        <CreditCard className="w-10 h-10 text-[#A78BFA] mb-4" />
        <h2 className="font-head font-700 text-2xl text-slate-50 mb-2">Card / Apple Pay</h2>
        <p className="text-sm text-slate-400 mb-6">Auto-renewing subscription. Apple Pay & Google Pay appear automatically on supported devices. Cancel anytime from your dashboard.</p>

        <div className="flex items-end gap-2 mb-4">
          <span className="font-head font-900 text-4xl" style={{ color }} data-testid="quote-usd">{quote ? `$${quote.usd}` : "…"}</span>
          <span className="text-sm text-slate-500 mb-1">USD / cycle</span>
        </div>

        {/* Price Lock countdown */}
        <div className={`inline-flex items-center gap-2 mb-8 text-sm rounded-full px-3 py-1.5 border ${expired ? "border-rose-400/40 text-rose-300" : "border-amber-300/40 text-[#F5C842]"}`} data-testid="price-lock">
          <Lock className="w-4 h-4" />
          {fetching ? "Fetching price…" : expired ? "Price expired" : `Price locked · ${mmss}`}
        </div>

        {expired ? (
          <button
            data-testid="refresh-quote-btn"
            onClick={fetchQuote}
            className="w-full rounded-xl py-3 text-sm font-medium bg-[#F5C842] text-slate-900 hover:-translate-y-0.5 transition-transform duration-300 flex items-center justify-center gap-2"
          >
            <RefreshCw className="w-5 h-5" /> Refresh price
          </button>
        ) : (
          <button
            data-testid="pay-card-btn"
            onClick={pay}
            disabled={loading || !quote}
            className="w-full rounded-xl py-3 text-sm font-medium text-slate-900 hover:-translate-y-0.5 transition-transform duration-300 flex items-center justify-center gap-2 disabled:opacity-60"
            style={{ background: color }}
          >
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <CreditCard className="w-5 h-5" />}
            {loading ? "Redirecting…" : "Continue to checkout"}
          </button>
        )}
      </div>
    </div>
  );
}

function BtcPay({ tier, color, onBack }) {
  const [invoice, setInvoice] = useState(null);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);
  const [txid, setTxid] = useState("");
  const [verifying, setVerifying] = useState(false);

  useEffect(() => {
    let mounted = true;
    api.post("/invoices", { tier })
      .then((r) => { if (mounted) setInvoice(r.data.invoice); })
      .catch((e) => { if (mounted) setError(apiErr(e)); });
    return () => { mounted = false; };
  }, [tier]);

  const copy = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success("Copied to clipboard");
    setTimeout(() => setCopied(false), 1800);
  };

  const verify = async (e) => {
    e.preventDefault();
    if (!txid.trim()) return;
    setVerifying(true);
    try {
      const { data } = await api.post(`/invoices/${invoice.id}/verify`, { txid: txid.trim() });
      setInvoice(data.invoice);
      toast.success("Payment verified on-chain! Subscription unlocked.");
    } catch (err) {
      toast.error(apiErr(err));
    } finally {
      setVerifying(false);
    }
  };

  const status = invoice?.status || "pending";
  const confirmed = status === "confirmed";
  const btcUri = invoice ? `bitcoin:${invoice.address}?amount=${invoice.expected_btc}` : "";

  if (error) return (
    <div>
      <ChangeMethod onBack={onBack} />
      <div className="rounded-2xl border border-rose-400/40 bg-white/[0.03] backdrop-blur-md p-8 text-rose-300" data-testid="payment-error">{error}</div>
    </div>
  );
  if (!invoice) return <div className="flex items-center gap-3 text-slate-400"><Loader2 className="w-6 h-6 animate-spin" /> Generating invoice…</div>;

  return (
    <div>
      <ChangeMethod onBack={onBack} />
      <div className="grid md:grid-cols-2 gap-6">
        <div className="rounded-2xl border border-amber-300/25 bg-white/[0.03] backdrop-blur-md p-8" style={{ boxShadow: "0 0 60px rgba(245,200,66,0.08)" }}>
          <div className="bg-white p-4 w-fit mx-auto mb-6 rounded-xl" data-testid="btc-qr">
            <QRCode value={btcUri} size={200} />
          </div>
          <p className="text-xs uppercase tracking-widest text-[#F5C842] mb-2">Send at least</p>
          <div className="flex items-center gap-2 mb-6">
            <span className="font-head font-700 text-2xl text-slate-50" data-testid="btc-amount">{invoice.expected_btc}</span>
            <span className="text-lg text-slate-400">BTC</span>
            <button onClick={() => copy(String(invoice.expected_btc))} data-testid="copy-amount-btn" className="ml-auto p-2 rounded-lg border border-white/15 text-slate-300 hover:border-[#F5C842] hover:text-[#F5C842] transition-colors duration-300">
              <Copy className="w-4 h-4" />
            </button>
          </div>
          <p className="text-xs text-slate-500 mb-1">≈ {invoice.expected_sats.toLocaleString()} sats</p>
          <p className="text-xs uppercase tracking-widest text-[#F5C842] mt-6 mb-2">To address</p>
          <div className="flex items-center gap-2 bg-black/30 border border-white/10 rounded-xl p-3">
            <code className="text-xs text-slate-300 break-all" data-testid="btc-address">{invoice.address}</code>
            <button onClick={() => copy(invoice.address)} data-testid="copy-btc-address" className="shrink-0 p-2 rounded-lg border border-white/15 text-slate-300 hover:border-[#F5C842] hover:text-[#F5C842] transition-colors duration-300">
              {copied ? <Check className="w-4 h-4 text-[#2DD4BF]" /> : <Copy className="w-4 h-4" />}
            </button>
          </div>
        </div>

        <div className="rounded-2xl border border-violet-400/25 bg-white/[0.03] backdrop-blur-md p-8 flex flex-col" style={{ boxShadow: "0 0 60px rgba(167,139,250,0.08)" }}>
          <h2 className="font-head font-700 text-xl text-slate-50 mb-6">Proof of payment</h2>
          {confirmed ? (
            <div className="flex-1 flex flex-col" data-testid="payment-status">
              <div className="flex items-center gap-3 mb-4">
                <CircleCheck className="w-8 h-8 text-[#2DD4BF]" />
                <span className="font-head font-700 text-2xl text-[#2DD4BF]">Verified</span>
              </div>
              <p className="text-sm text-slate-400 mb-6">Payment confirmed on-chain. Your subscription is now active.</p>
              <Link to="/home" data-testid="play-now-btn" className="mt-auto text-center rounded-xl py-3 text-sm font-medium bg-[#2DD4BF] text-slate-900 hover:-translate-y-0.5 transition-transform duration-300">
                Continue
              </Link>
            </div>
          ) : (
            <form onSubmit={verify} className="flex-1 flex flex-col" data-testid="payment-status">
              <div className="space-y-6 mb-6">
                <Step done label="Invoice created" sub="Send the BTC from your wallet" />
                <Step active={!!txid} label="Broadcast on-chain" sub="Grab the transaction hash (txid) from your wallet" />
                <Step label="Submit proof" sub="Paste the txid below and verify" />
              </div>
              <label className="text-xs uppercase tracking-widest text-[#A78BFA] block mb-1.5">Transaction hash (txid)</label>
              <input
                data-testid="txid-input"
                value={txid}
                onChange={(e) => setTxid(e.target.value)}
                placeholder="e.g. 3a1b2c…64 hex chars"
                className="w-full bg-black/30 border border-white/10 rounded-xl px-4 py-2.5 text-slate-100 text-xs focus:border-[#A78BFA] focus:ring-1 focus:ring-[#A78BFA]/40 outline-none transition-colors duration-300 mb-4 break-all"
              />
              <button
                type="submit"
                disabled={verifying || !txid.trim()}
                data-testid="verify-payment-btn"
                className="rounded-xl py-3 text-sm font-medium text-slate-900 hover:-translate-y-0.5 transition-transform duration-300 inline-flex items-center justify-center gap-2 disabled:opacity-50"
                style={{ background: color }}
              >
                {verifying ? <Loader2 className="w-5 h-5 animate-spin" /> : <ShieldCheck className="w-5 h-5" />}
                {verifying ? "Verifying…" : "Verify payment"}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

function Step({ done, active, label, sub }) {
  return (
    <div className="flex items-start gap-3">
      {done ? <CircleCheck className="w-6 h-6 text-[#2DD4BF] shrink-0" />
            : <CircleDashed className={`w-6 h-6 shrink-0 ${active ? "text-[#F5C842]" : "text-slate-600"}`} />}
      <div>
        <p className={`text-sm font-medium ${done ? "text-slate-100" : active ? "text-[#F5C842]" : "text-slate-500"}`}>{label}</p>
        <p className="text-xs text-slate-500">{sub}</p>
      </div>
    </div>
  );
}
